<template>
    <EditComponent
        ref="editComponentRef"
        dialog-id="editSkillSetDialog"
        open-dialog-event="skill-set:edit-mode"
        updated-event="skill-set:updated"
        :open-dialog-post-function="handleDialogPostOpen"
        confirm-data-testid="profile-edition-section-skill-set-cta-submit"
        :confirm-function="handleDialogConfirm"
        :confirm-enabled="isEnabledCtaConfirm"
        :track-section-opened="EditionOpenedSection.SKILL_SET"
        :track-existing-element="true"
        data-testid="profile-edition-section-skill-set"
    >
        <template #edit-header>
            <p>{{ t('profile.section.skill.set.edition.title') }}</p>
            <div class="profile-edition-skill-set__header__warnings" data-testid="profile-edition-section-skill-set-header-warnings"></div>
        </template>
        <template #edit-body>
            <FetchErrorHighlight v-if="error" data-testid="profile-edition-section-skill-set-functional-skills-error">
                <template #technical-error>{{ error }}</template>
            </FetchErrorHighlight>
            <template v-else>
                <VJoyHighlight
                    v-if="!hasSelectedSkills"
                    level="info"
                    display-icon
                    class="profile-edition-skill-set__no-skills-highlight"
                    data-testid="profile-edition-section-skill-set-no-skills-highlight"
                >
                    {{ t('profile.section.skill.set.edition.no.skills.info.highlight') }}
                </VJoyHighlight>
                <div id="profile-edition-skill-set-section-2" data-testid="profile-edition-skill-set-section-2">
                    <EditComponentSubSection data-testid="profile-edition-selected-skills">
                        <template #sub-body>
                            <ShowMoreByMaxHeight
                                class="profile-edition-selected-skills__show-more"
                                data-testid="profile-edition-selected-skills-show-more"
                                :max-height="213"
                                :display-all-in-edition-mode="false"
                                :label="t('profile.section.skill.set.show.all.label.v2', {count: currentSelectedSkills.length})"
                                :locked="{allowHref: false, label: ''}"
                            >
                                <div class="profile-edition-skills__list">
                                    <ProfileSkillSetItem
                                        v-for="(selectedSkill, index) in currentSelectedSkills"
                                        :key="`profile-edition-selected-skill-${index}`"
                                        :data-testid="`profile-edition-selected-skill-${index}`"
                                        :disabled="false"
                                        :favorite="false"
                                        :is-enabled-mode-remove="true"
                                        :is-enabled-tooltip="false"
                                        :mode-favorite="topSkillAdditionMode"
                                        @favorite:add="handleSelectedSkillFavoriteAdd(selectedSkill)"
                                        @remove="handeSelectedSkillRemove(selectedSkill)"
                                    >
                                        {{ selectedSkill.label }}
                                    </ProfileSkillSetItem>
                                </div>
                            </ShowMoreByMaxHeight>
                        </template>
                    </EditComponentSubSection>
                </div>
                <VJoyText class="profile-edition-skill-set__title" preset="primary-450">
                    {{ t('profile.section.skill.set.edition.globalskills.add.label') }}
                </VJoyText>
                <div id="profile-edition-skill-set-section-3" ref="refComponentSection3" data-testid="profile-edition-skill-set-section-3"></div>
                <div id="profile-edition-skill-set-section-4" ref="refComponentSection4" data-testid="profile-edition-skill-set-section-4"></div>
                <Teleport :to="refComponentTargetFunctionalSkills" :disabled="!refComponentTargetFunctionalSkills">
                    <EditComponentSubSection data-testid="profile-edition-functional-skills">
                        <template #sub-body>
                            <EditFunctionalSkills
                                :model-value="currentFunctionalSkills"
                                :is-skill-addition-enabled="isAbleToAddNewSkills"
                                :rules-functional-skills-count-max="skillSetFunctionalSkills.max"
                                :rules-functional-skills-count-min="skillSetFunctionalSkills.min"
                                :show-min-error="showFunctionalSkillsMinError"
                                @add="handleEditFunctionalSkillsAdd"
                                @remove="handleEditFunctionalSkillsRemove"
                                @open-categories-edit="handleOpenCategoriesEdit"
                            />
                        </template>
                    </EditComponentSubSection>
                </Teleport>
                <Teleport :to="refComponentTargetGlobalSkills" :disabled="!refComponentTargetGlobalSkills">
                    <EditComponentSubSection data-testid="profile-edition-global-skills">
                        <template #sub-body>
                            <EditGlobalSkills
                                :model-value="currentGlobalSkillsAddedToSelectedSkills"
                                :disabled="!isAbleToAddNewSkills"
                                :disabled-label="t('profile.section.skill.set.edition.globalskills.add.max.reached')"
                                :label="t('profile.section.skill.set.edition.globalskills.search.label')"
                                :placeholder="t('profile.section.skill.set.edition.globalskills.add.placeholder')"
                                :rules-global-skill-label-max-length="skillSetGlobalSkillLabel.max"
                                :rules-global-skill-label-min-length="skillSetGlobalSkillLabel.min"
                                :show-occurences="true"
                                @add="handleEditGlobalSkillsAdd"
                            />
                        </template>
                    </EditComponentSubSection>
                </Teleport>
            </template>
        </template>
    </EditComponent>
</template>
<script setup lang="ts">
    import {computed, nextTick, ref, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useAsyncData, useTranslation} from '#imports';
    import {pushVJoySnackbar, VJoyHighlight, VJoyText} from '@maltjoy/core-vue';
    import ShowMoreByMaxHeight from '~/components/_common/show-more/ShowMoreByMaxHeight.vue';
    import {eventBus} from '~/config/events';
    import {EditionOpenedSection, EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import type {Skill} from '~/openapi';
    import {ProfileVertical} from '~/types/domain/Profile';
    import EditGlobalSkills from './EditGlobalSkills.vue';
    import {useSkillSetState} from './useSkillSetState';
    import FetchErrorHighlight from '@/components/_common/FetchErrorHighlight.vue';
    import ProfileSkillSetItem from '@/components/_common/skill-set/ProfileSkillSetItem.vue';
    import EditComponentSubSection from '@/components/edit/_common/EditComponentSubSection.vue';
    import EditComponent from '@/components/edit/EditComponent.vue';
    import EditFunctionalSkills from '@/components/edit/skill-set/EditFunctionalSkills.vue';
    import {useProfileStore} from '@/store/profileStore';
    import {useRulesStore} from '@/store/rules/rulesStore';
    import {useSkillSetStore} from '@/store/skill-set/skillSetStore';

    const {t} = useTranslation();
    const {profilePreferredCategory, profilePreferredFamily, profile} = storeToRefs(useProfileStore());
    const rulesStore = useRulesStore();
    const {saveSkillSet} = useSkillSetStore();
    const {skillSetGlobalSkillLabel, skillSetSelectedSkills, skillSetFunctionalSkills} = storeToRefs(useRulesStore());
    const {
        currentGlobalSkillsAddedToSelectedSkills,
        currentRemainingFunctionalSkills,
        currentGlobalSkills,
        currentSelectedSkills,
        currentTopSkills,
        addSelectedSkill,
        addTopSkill,
        removeSelectedSkill,
        removeTopSkill,
        initializeStateUsingStoreValues,
    } = useSkillSetState();

    const editComponentRef = ref<InstanceType<typeof EditComponent> | null>(null);
    const showFunctionalSkillsMinError = ref(false);
    const isStrategyProfile = computed(() => profile.value?.vertical === ProfileVertical.STRATEGY);

    const {error} = await useAsyncData(
        'edit_skill-set',
        async () => {
            const rules = await rulesStore.init({profileId: profile.value?.profileId});

            return {rules};
        },
        {deep: true},
    );

    // Sections order
    const refComponentSection3 = ref();
    const refComponentSection4 = ref();

    const refComponentTargetFunctionalSkills = refComponentSection4;
    const refComponentTargetGlobalSkills = refComponentSection3;

    // Functional skills
    const currentFunctionalSkills = computed(() => currentRemainingFunctionalSkills.value);

    watch(
        () => currentFunctionalSkills.value.length,
        (length) => {
            if (length >= skillSetFunctionalSkills.value.min) {
                showFunctionalSkillsMinError.value = false;
            }
        },
    );

    // Selected skills
    const hasSelectedSkills = computed(() => currentSelectedSkills.value.length > 0);
    const selectedSkillsCount = computed(() => currentSelectedSkills.value.length || 0);

    // Top skills
    const topSkillAdditionMode = computed(() => undefined);
    const topSkillsCount = computed(() => currentTopSkills.value.length || 0);

    // Top & Selected skills
    const selectedAndTopSkillsCount = computed(() => topSkillsCount.value + selectedSkillsCount.value);

    const hasEnoughSelectedAndTopSkills = computed(() => selectedAndTopSkillsCount.value >= skillSetSelectedSkills.value.min);

    const isSelectedAndTopSkillsCountValid = computed(
        (): boolean => hasEnoughSelectedAndTopSkills.value && selectedAndTopSkillsCount.value <= skillSetSelectedSkills.value.max,
    );

    const isAbleToAddNewSkills = computed((): boolean => selectedAndTopSkillsCount.value < skillSetSelectedSkills.value.max);

    // CTAs
    const isEnabledCtaConfirm = computed(() => true);

    function addSkillToTopSkills(skillToAddToTop: Skill) {
        if (topSkillAdditionMode.value === 'enabled') {
            removeSelectedSkill(skillToAddToTop);
            addTopSkill(skillToAddToTop);
        }
    }

    // Event handlers
    function handleEditGlobalSkillsAdd(globalSkillToAdd: Skill) {
        addSelectedSkill(globalSkillToAdd);
    }

    function handleEditFunctionalSkillsAdd(functionalSkillToAdd: Skill) {
        addSelectedSkill(functionalSkillToAdd);
    }

    function handleEditFunctionalSkillsRemove(functionalSkillToRemove: Skill) {
        removeTopSkill(functionalSkillToRemove);
        removeSelectedSkill(functionalSkillToRemove);
    }

    function handeSelectedSkillRemove(skillToRemoveFromSelected: Skill) {
        removeSelectedSkill(skillToRemoveFromSelected);
    }

    function handleSelectedSkillFavoriteAdd(skillToAddToTop: Skill) {
        addSkillToTopSkills(skillToAddToTop);
    }

    function handleSelectedSkillFavoriteRemove(skillToRemoveFromTop: Skill) {
        removeTopSkill(skillToRemoveFromTop);
        addSelectedSkill(skillToRemoveFromTop);
    }

    function handleOpenCategoriesEdit() {
        editComponentRef.value?.close();
        eventBus.emit('categories:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
    }

    function handleDialogPostOpen() {
        initializeStateUsingStoreValues();
        showFunctionalSkillsMinError.value = false;
    }

    async function handleDialogConfirm() {
        if (isStrategyProfile.value && currentFunctionalSkills.value.length < skillSetFunctionalSkills.value.min) {
            showFunctionalSkillsMinError.value = true;
            await nextTick();
            document.getElementById('profile-edition-section-skill-set-functional-skills-min-error')?.scrollIntoView({behavior: 'smooth'});
            return false;
        } else if (selectedAndTopSkillsCount.value < skillSetSelectedSkills.value.min) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.section.expertises.edit.globalSkills.error.min', {0: skillSetSelectedSkills.value.min}),
                    level: 'error',
                },
            });
            return false;
        } else if (selectedAndTopSkillsCount.value > skillSetSelectedSkills.value.max) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.section.expertises.edit.globalSkills.error.max', {0: skillSetSelectedSkills.value.max}),
                    level: 'error',
                },
            });
            return false;
        } else if (currentGlobalSkills.value.some((skill) => skill.label.length > skillSetGlobalSkillLabel.value.max)) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.creation.completion.wizard.details.skills.error.label.too.long', {0: skillSetGlobalSkillLabel.value.max}),
                    level: 'error',
                },
            });

            return false;
        } else {
            try {
                await saveSkillSet(currentSelectedSkills.value, currentTopSkills.value);
            } catch (error) {
                pushVJoySnackbar({
                    props: {
                        message: t('generic.error.unexpected'),
                        level: 'error',
                    },
                });
                return false;
            }
        }
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .profile-edition-empty-skill-item {
        .profile-edition__skills_item__tag__content {
            max-width: fit-content;
        }
    }

    .profile-edition-skill-set {
        &__header {
            justify-content: flex-start;
            display: flex;
            align-items: center;

            &__counter {
                line-height: 0;

                &__label {
                    @include text.getFontBySize(primary-400);
                    color: var(--joy-color-secondary-30);
                }
            }

            &__warnings {
                margin-top: var(--joy-core-spacing-4);
            }
        }

        &__title {
            padding-bottom: var(--joy-core-spacing-2) !important;
        }

        &__section {
            &__title {
                display: flex;
                @include text.getFontBySize(primary-400);
                font-weight: var(--joy-font-weight-bold);
                color: var(--joy-color-text-heading);

                &__tooltip {
                    margin-left: var(--joy-core-spacing-2);
                    margin-right: var(--joy-core-spacing-2);
                }
            }

            &__subtitle {
                margin-top: var(--joy-core-spacing-4);
            }
        }
    }

    .profile-edition-freetext-skills {
        &__label {
            @include text.getFontBySize(primary-300);
        }
    }

    .profile-edition-skills {
        &__alert {
            margin-bottom: var(--joy-core-spacing-4);
            color: var(--joy-color-error-50);
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-1);
        }

        &__list {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            justify-content: flex-start;
            gap: var(--joy-core-spacing-4);
            margin-top: var(--joy-core-spacing-1);
        }
    }

    .profile-edition-selected-skills {
        &__show-more {
            padding: var(--joy-core-spacing-2);
        }
    }

    .top-skills-title {
        display: flex;

        &__tooltip {
            margin-left: var(--joy-core-spacing-2);
        }
    }
</style>
